import type { ReactNode } from 'react';
import { T } from 'gt-react';
import {
  CaretDownIcon,
  CircleNotchIcon,
  DownloadSimpleIcon,
  FileCsvIcon,
  FileXlsIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type ExportFormat = 'csv' | 'xlsx';

// "Export as…" dropdown (CSV / XLSX). Each caller wires its own export handler
// so the download reflects that view; the underlying endpoints stream the
// result (gzip when accepted).
export function ExportMenu({
  onExport,
  isPending,
  disabled = false,
  className,
  csvLabel,
}: {
  onExport: (format: ExportFormat) => void;
  isPending: boolean;
  // Nothing to download (e.g. the caller's filters select no rows at all).
  disabled?: boolean;
  // What the text-file option is called, for a caller whose options change what
  // that file is (the review page writes a tab-separated one on request). The
  // format passed back is still `csv` — this only names it.
  csvLabel?: ReactNode;
  // Extra trigger classes. The trigger is sized for a toolbar; a caller placing
  // it in a narrow panel (the review page's actions sidebar) stretches it here.
  className?: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isPending || disabled}
          className={className}
        >
          {isPending ? (
            <CircleNotchIcon className="h-4 w-4 animate-spin" />
          ) : (
            <DownloadSimpleIcon className="h-4 w-4" />
          )}
          <T context="item catalog export dropdown button">Export as…</T>
          <CaretDownIcon className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => onExport('csv')}
          className="cursor-pointer gap-2"
        >
          <FileCsvIcon className="h-4 w-4" />
          {csvLabel ?? <T context="item catalog export CSV option">CSV</T>}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onExport('xlsx')}
          className="cursor-pointer gap-2"
        >
          <FileXlsIcon className="h-4 w-4" />
          <span className="flex flex-col">
            <T context="item catalog export Excel option">Excel (.xlsx)</T>
            <span className="text-muted-foreground text-xs font-normal">
              <T context="item catalog export Excel option hint about oversized files">
                Oversized files download as a zip of smaller workbooks
              </T>
            </span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
